import { imageEffectSamples } from "@saas/shared/config/image-effect-samples";
import { buildR2AssetsImageUrl } from "../../shared/lib/image-url-builder";
import type { Template } from "../types";

/**
 * 放在根目录的模板（10/29 肌肉页起，老公定网址不带 /templates/）：页面、生图、落库 page 都用根路径，
 * /templates/{id} 在 next.config.ts 里 301 过去
 */
const ROOT_TEMPLATE_IDS = [
	"ai-muscle-generator",
	"ai-big-butt",
	"ai-feet-generator",
];

export function getTemplatePath(templateId: string): string {
	return ROOT_TEMPLATE_IDS.includes(templateId)
		? `/${templateId}`
		: `/templates/${templateId}`;
}

/** 根目录网址最后一段是不是模板（工作台按网址认模板用） */
export function getRootTemplateId(segment: string | undefined): string | null {
	return segment && ROOT_TEMPLATE_IDS.includes(segment) ? segment : null;
}

// 肌肉页四档预设：imgveo 原句在 NB 2.1 上 Toned / Athletic 几乎不变，Toned / Athletic 是改写过的
// （docs/research/ai-muscle-research-2026-10-29.md §3）。保护后缀由服务端按来源页补（generator.ts）
const MUSCLE_PROMPTS = {
	toned: "Make the person in the photo clearly more toned and fit — visibly defined arm and shoulder muscles, slightly broader shoulders and a lean, fit physique; the change should be obvious at a glance but still natural and realistic",
	athletic:
		"Give the person in the photo a clearly athletic, muscular physique — noticeably bigger, well-defined arms and shoulders with visible muscle separation, like a fitness model; realistic",
	bodybuilder:
		"Transform the person in the photo into a competition bodybuilder — dramatically larger chest, shoulders and arms, an entertaining exaggeration with a proud grin",
	superhero:
		"Give the person in the photo a fantasy superhero physique — powerfully sculpted arms and shoulders with a subtle heroic glow, clearly playful fantasy",
};

const muscleRef = (name: string) =>
	buildR2AssetsImageUrl(`home/showcase/ai-muscle/refs/${name}-1k.webp`);
// 结果图都在本页出（NB 2.1 2K，10/29），压成 1024 宽 webp
const muscleResult = (name: string) =>
	buildR2AssetsImageUrl(`home/showcase/ai-muscle/results/${name}-1k.webp`);

const muscleExamples = [
	{
		before: muscleRef("man-tee"),
		after: muscleResult("man-tee-bodybuilder"),
		prompt: MUSCLE_PROMPTS.bodybuilder,
	},
	{
		before: muscleRef("woman-tank"),
		after: muscleResult("woman-tank-athletic-v2"),
		prompt: MUSCLE_PROMPTS.athletic,
	},
	{
		before: muscleRef("man-tee"),
		after: muscleResult("man-tee-superhero"),
		prompt: MUSCLE_PROMPTS.superhero,
	},
	{
		before: muscleRef("woman-tank"),
		after: muscleResult("woman-tank-toned"),
		prompt: MUSCLE_PROMPTS.toned,
	},
];

const muscleUseCases = [
	{ key: "fitnessGoal", ref: "beach-man", preset: "athletic" },
	{ key: "memes", ref: "bbq-uncle", preset: "bodybuilder" },
	{ key: "womenStrength", ref: "gym-woman", preset: "athletic" },
	{ key: "profile", ref: "street-polo", preset: "toned" },
] as const;

// 大臀页三档预设（docs/research/ai-big-butt-research-2026-10-29.md §3，Seedream 5 Flash 实测）。
// 保护后缀（脸、衣服、背景不动，穿着衣服）由服务端按来源页补（generator.ts）
const BUTT_PROMPTS = {
	subtle: "Give the person a subtly fuller, perkier butt with a gentle lift and a slightly rounder hip curve; a natural, believable enhancement that is still visible at a glance",
	curvy: "Make the person's butt and hips noticeably fuller, rounder and more lifted, with a clear curvy waist-to-hip silhouette; the change should be obvious at a glance but still realistic",
	extra: "Give the person a dramatically bigger, very round and lifted butt with wide curvy hips and a tiny-waist hourglass silhouette, an entertaining exaggeration that still looks photorealistic",
};

const buttRef = (name: string) =>
	buildR2AssetsImageUrl(`home/showcase/ai-big-butt/refs/${name}-1k.webp`);
// 结果图都在本页出（Seedream 5 Flash 2K，10/30），压成 1024 宽 webp。带 -v1：不带版本的地址
// 在上传前被 dev 页面请求过，R2 边缘缓存了 404。10/31 老公：黑紧身裤看不出变化、牛仔裤那位身材一般，
// 换成 -v2 两张新照片（浅粉紧身裤、身材好些的牛仔裤），男生场景卡撤掉
const buttResult = (name: string) =>
	buildR2AssetsImageUrl(`home/showcase/ai-big-butt/results/${name}-v1-1k.webp`);

const buttExamples = [
	{
		before: buttRef("woman-leggings-v2"),
		after: buttResult("woman-leggings-v2-extra"),
		prompt: BUTT_PROMPTS.extra,
	},
	{
		before: buttRef("woman-jeans-v2"),
		after: buttResult("woman-jeans-v2-curvy"),
		prompt: BUTT_PROMPTS.curvy,
	},
	{
		before: buttRef("woman-dress"),
		after: buttResult("woman-dress-subtle"),
		prompt: BUTT_PROMPTS.subtle,
	},
];

const buttUseCases = [
	{ key: "gymFit", ref: "woman-leggings-v2", preset: "curvy" },
	{ key: "denim", ref: "woman-jeans-v2", preset: "subtle" },
	{ key: "nightOut", ref: "woman-dress", preset: "curvy" },
] as const;

// 脚图页六档预设：11/01 老公要 nanomaker 那种「上传照片 → 脚部特写」，风格参考他给的小红书图（海滩俯拍、
// 车里脚搭头枕、草地脚底、红甲特写、动漫职场高跟）。都按照片写成 "the person"；不传图时服务端只补安全后缀，
// 按文字新造一个人。镜头要写死「贴地 / 膝盖以下」，只写「把焦点移到脚上」会出全身照（11/01 实测）。
// 成人、穿衣、非色情、五个脚趾的后缀由服务端按来源页补（generator.ts），这里不重复写
const FEET_PROMPTS = {
	soles: "Low-angle shot from the floor: the person sits back in a wooden chair with bare feet stretched toward the camera, soles in sharp focus in the foreground, the person in soft focus behind, keep original body proportions and a natural, relaxed posture, warm lamp-lit room",
	beach: "Top-down first-person view looking straight down at the person's own bare feet standing in shallow foamy waves on wet golden sand, jeans rolled up to mid-calf, glossy coral pedicure, water swirling around the toes, bright sunlight, realistic phone photo",
	car: "Inside a car with cream leather seats, the person reclines in the passenger seat with long bare legs stretched up and bare feet resting on the headrest, glossy red pedicure, framed from the thighs to the toes, bright summer daylight, soft glamorous look",
	grass: "The person lies on a lush green lawn, framed from the knees down, long bare legs, one bare foot crossed over the other with the soles toward the camera, a thin silver anklet, glossy pedicure, soft golden afternoon light",
	pedicure:
		"Close-up of the person's bare feet resting on a flat brown leather sandal on a grey carpet, fresh glossy red pedicure, a small bottle of red nail polish beside them, a red string anklet, hard sunlight with crisp shadows",
	anime: "Anime illustration of the person sitting with legs crossed on a black leather office sofa, wearing a grey blazer and a pencil skirt, long bare legs, one black high heel dangling from her toes, the other foot in a heel, soft office light, clean webtoon style",
};

const feetRef = (name: string) =>
	buildR2AssetsImageUrl(`home/showcase/ai-feet/refs/${name}-1k.webp`);
// 结果图都在本页出（NB 2.1 2K，原图各是不同的女生），压成 1024 宽 webp
const feetResult = (name: string) =>
	buildR2AssetsImageUrl(`home/showcase/ai-feet/samples/${name}-1k.webp`);

// 全是上传照片的用法：六个预设各一条，最后两条是第一轮探测留下的落地窗、阳台（prompt 原样）
const feetExamples = [
	{ ref: "woman-sofa", result: "woman-sofa-chair", prompt: FEET_PROMPTS.soles },
	{ ref: "woman-jeans", result: "woman-jeans-beach", prompt: FEET_PROMPTS.beach },
	{ ref: "asian-a", result: "asian-a-car", prompt: FEET_PROMPTS.car },
	{ ref: "woman-dress", result: "woman-dress-grass", prompt: FEET_PROMPTS.grass },
	{ ref: "asian-b", result: "asian-b-pedicure", prompt: FEET_PROMPTS.pedicure },
	{ ref: "woman-jeans", result: "woman-jeans-anime", prompt: FEET_PROMPTS.anime },
	{
		ref: "woman-sofa",
		result: "woman-sofa-window",
		prompt: "Close-up from knee height of the person standing barefoot on a light stone floor by a floor-to-ceiling window, framed from the knees down so the bare feet are the subject, soft morning light, realistic shadows, clean editorial style",
	},
	{
		ref: "woman-sofa",
		result: "woman-sofa-balcony",
		prompt: "Camera at floor level, close-up of the person's bare feet standing on a sunny terracotta balcony floor, framed from mid-calf down so the feet fill the frame, red nail polish, rooftops softly blurred behind, natural daylight, refined travel photography",
	},
].map(({ ref, result, prompt }) => ({
	before: feetRef(ref),
	after: feetResult(result),
	prompt,
}));

export const templates: Template[] = [
	{
		id: "ai-clothes-changer",
		publishedAt: "2026-01-10",
		updatedAt: "2026-10-26",
		name: "AI Clothes Changer",
		description: "Try on clothes virtually with AI",
		prompt: "Change outfit to [describe the desired clothing style, color, and type], realistic fabric textures, natural lighting, professional photography quality",
		category: "personal",
		thumbnail: buildR2AssetsImageUrl(
			"templates/ai-clothes-changer/carousel/output-model-tryon-2.webp",
		),
		exampleImages: [
			buildR2AssetsImageUrl(
				"templates/ai-clothes-changer/carousel/input-tryon-model-2.webp",
			),
			buildR2AssetsImageUrl(
				"templates/ai-clothes-changer/carousel/input-worn-product-2.webp",
			),
		],
		behavior: {
			defaultAspectRatio: "1:1",
			disableOriginalAspectRatio: true,
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/carousel/input-tryon-model-2.webp",
						),
						buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/carousel/input-worn-product-2.webp",
						),
					],
					result: buildR2AssetsImageUrl(
						"templates/ai-clothes-changer/carousel/output-model-tryon-2.webp",
					),
				},
				style: {
					theme: "professional",
				},
			},
			model: {
				default: "v3.0",
			},
		},
		marketingContent: {
			useCases: [
				{
					titleKey:
						"templates.ai-clothes-changer.useCases.tryBeforeBuy.title",
					descriptionKey:
						"templates.ai-clothes-changer.useCases.tryBeforeBuy.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/try-before-buy-before.webp?v=e48425d39f",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/try-before-buy-after.webp?v=e7c3619165",
						),
						prompt: "Change the outfit to a cropped red tweed jacket with gold buttons and high-waisted white wide-leg trousers, keeping the face, pose, sneakers and background unchanged",
					},
				},
				{
					titleKey:
						"templates.ai-clothes-changer.useCases.socialMedia.title",
					descriptionKey:
						"templates.ai-clothes-changer.useCases.socialMedia.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/social-media-before.webp?v=88bb9c2049",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/social-media-after.webp?v=417a2e4228",
						),
						prompt: "Change the outfit to a cobalt blue tailored blazer and matching wide-leg trousers over a white top, keeping the face, pose, sneakers and background unchanged",
					},
				},
				{
					titleKey:
						"templates.ai-clothes-changer.useCases.ecommerce.title",
					descriptionKey:
						"templates.ai-clothes-changer.useCases.ecommerce.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/ecommerce-before.webp?v=699146749c",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/ecommerce-after.webp?v=637f523a1a",
						),
						prompt: "Change the top to a dark green suede zip-up jacket over a cream knit crewneck, keeping the grey trousers, sneakers, face, pose and background unchanged",
					},
				},
				{
					titleKey:
						"templates.ai-clothes-changer.useCases.eventPlanning.title",
					descriptionKey:
						"templates.ai-clothes-changer.useCases.eventPlanning.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/event-planning-before.webp?v=77836a3d86",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/event-planning-after.webp?v=286acbe94b",
						),
						prompt: "Change the outfit to a floor-length emerald green satin one-shoulder gown with soft draping, keeping the face, hair, pose and background unchanged",
					},
				},
				{
					titleKey:
						"templates.ai-clothes-changer.useCases.professionalPhoto.title",
					descriptionKey:
						"templates.ai-clothes-changer.useCases.professionalPhoto.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/fashion-design-before.webp?v=0dc5257d51",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/fashion-design-after.webp?v=c874904474",
						),
						prompt: "Change the outfit to an ivory tailored suit with a wide-lapel blazer, matching wide-leg trousers and ivory pointed heels, keeping the face, pose and background unchanged",
					},
				},
				{
					titleKey:
						"templates.ai-clothes-changer.useCases.personalStyling.title",
					descriptionKey:
						"templates.ai-clothes-changer.useCases.personalStyling.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/personal-style-before.webp?v=79a5ed0751",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/personal-style-after.webp?v=57ce9b24b5",
						),
						prompt: "Change the top to a cropped rust suede jacket over a tucked-in cream shirt with a brown leather belt, keeping the jeans, sneakers, face, pose and background unchanged",
					},
				},
			],
			carouselSlides: [
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/showcase-evening-before.webp?v=eaa0c956da",
						),
						buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/editorial-2026-10-26/showcase-evening-garment.webp?v=9c2bc1ac1f",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-clothes-changer/editorial-2026-10-26/showcase-evening-after.webp?v=6edc7d1e8f",
					),
					prompt: "Transform to elegant black evening dress, sophisticated styling, premium fabric details, studio lighting, professional fashion photography",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/carousel/input-tryon-model-2.webp",
						),
						buildR2AssetsImageUrl(
							"templates/ai-clothes-changer/carousel/input-worn-product-2.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-clothes-changer/carousel/output-model-tryon-2.webp",
					),
					prompt: "Change the outfit to the black bikini shown in the clothing reference, with natural fabric details and soft studio lighting.",
				},
			],
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.ai-clothes-changer.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.ai-clothes-changer.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.ai-clothes-changer.howItWorks.steps.describe.title",
					descriptionKey:
						"templates.ai-clothes-changer.howItWorks.steps.describe.description",
				},
				{
					step: 3,
					titleKey:
						"templates.ai-clothes-changer.howItWorks.steps.generate.title",
					descriptionKey:
						"templates.ai-clothes-changer.howItWorks.steps.generate.description",
				},
			],
			faqItems: [
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.isFree.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.isFree.answer",
				},
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.skills.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.skills.answer",
				},
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.processingTime.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.processingTime.answer",
				},
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.realistic.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.realistic.answer",
				},
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.privacy.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.privacy.answer",
				},
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.commercial.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.commercial.answer",
				},
				{
					questionKey:
						"templates.ai-clothes-changer.faq.items.deleteContent.question",
					answerKey:
						"templates.ai-clothes-changer.faq.items.deleteContent.answer",
				},
			],
			ctaTitleKey: "templates.ai-clothes-changer.cta.title",
			ctaDescriptionKey: "templates.ai-clothes-changer.cta.description",
		},
	},
	{
		id: "ai-background-remover",
		publishedAt: "2026-01-14",
		updatedAt: "2026-10-25",
		name: "AI Background Remover",
		description: "Remove image backgrounds instantly with AI",
		prompt: "",
		category: "personal",
		thumbnail: buildR2AssetsImageUrl(
			"templates/ai-background-remover/carousel/after-01.webp",
		),
		exampleImages: [
			buildR2AssetsImageUrl(
				"templates/ai-background-remover/carousel/before-01.webp",
			),
		],
		behavior: {
			defaultAspectRatio: "1:1",
			disableOriginalAspectRatio: true,
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						buildR2AssetsImageUrl(
							"templates/ai-background-remover/carousel/before-01.webp",
						),
					],
					result: buildR2AssetsImageUrl(
						"templates/ai-background-remover/carousel/after-01.webp",
					),
				},
				style: {
					theme: "professional",
				},
			},
			model: {
				default: "background-remover",
			},
			maxImages: 1,
			useCustomApi: true,
			customApiEndpoint: "/api/background-remover/remove",
			creditCost: 1,
			hidePrompt: true,
			hideAspectRatio: true,
			hideOutputCount: true,
		},
		marketingContent: {
			useCases: [
				{
					titleKey:
						"templates.ai-background-remover.useCases.ecommerce.title",
					descriptionKey:
						"templates.ai-background-remover.useCases.ecommerce.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/ecommerce-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/ecommerce-after.webp",
						),
					},
					icon: "shopping-bag",
					iconColor: "text-green-600 dark:text-green-400",
					bgGradient:
						"from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20",
				},
				{
					titleKey:
						"templates.ai-background-remover.useCases.graphicDesign.title",
					descriptionKey:
						"templates.ai-background-remover.useCases.graphicDesign.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/graphicDesign-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/graphicDesign-after.webp",
						),
					},
					icon: "palette",
					iconColor: "text-purple-600 dark:text-purple-400",
					bgGradient:
						"from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20",
				},
				{
					titleKey:
						"templates.ai-background-remover.useCases.socialMedia.title",
					descriptionKey:
						"templates.ai-background-remover.useCases.socialMedia.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/socialMedia-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/socialMedia-after.webp",
						),
					},
					icon: "users",
					iconColor: "text-blue-600 dark:text-blue-400",
					bgGradient:
						"from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20",
				},
				{
					titleKey:
						"templates.ai-background-remover.useCases.photography.title",
					descriptionKey:
						"templates.ai-background-remover.useCases.photography.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/photography-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/photography-after.webp",
						),
					},
					icon: "camera",
					iconColor: "text-amber-600 dark:text-amber-400",
					bgGradient:
						"from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20",
				},
				{
					titleKey:
						"templates.ai-background-remover.useCases.marketing.title",
					descriptionKey:
						"templates.ai-background-remover.useCases.marketing.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/marketing-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/marketing-after.webp",
						),
					},
					icon: "trending-up",
					iconColor: "text-indigo-600 dark:text-indigo-400",
					bgGradient:
						"from-indigo-50 to-indigo-100 dark:from-indigo-950/20 dark:to-indigo-900/20",
				},
				{
					titleKey:
						"templates.ai-background-remover.useCases.contentCreation.title",
					descriptionKey:
						"templates.ai-background-remover.useCases.contentCreation.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/contentCreation-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-background-remover/use-cases/contentCreation-after.webp",
						),
					},
					icon: "sparkles",
					iconColor: "text-rose-600 dark:text-rose-400",
					bgGradient:
						"from-rose-50 to-rose-100 dark:from-rose-950/20 dark:to-rose-900/20",
				},
			],
			carouselSlides: [
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-background-remover/carousel/before-01.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-background-remover/carousel/after-01.webp",
					),
					prompt: "Clean product cutout with a transparent background.",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-background-remover/carousel/before-02.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-background-remover/carousel/after-02.webp",
					),
					prompt: "A portrait cutout with a transparent background and preserved hair details.",
				},
			],
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.ai-background-remover.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.ai-background-remover.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.ai-background-remover.howItWorks.steps.describe.title",
					descriptionKey:
						"templates.ai-background-remover.howItWorks.steps.describe.description",
				},
				{
					step: 3,
					titleKey:
						"templates.ai-background-remover.howItWorks.steps.generate.title",
					descriptionKey:
						"templates.ai-background-remover.howItWorks.steps.generate.description",
				},
			],
			faqItems: [
				{
					questionKey:
						"templates.ai-background-remover.faq.items.isFree.question",
					answerKey:
						"templates.ai-background-remover.faq.items.isFree.answer",
				},
				{
					questionKey:
						"templates.ai-background-remover.faq.items.skills.question",
					answerKey:
						"templates.ai-background-remover.faq.items.skills.answer",
				},
				{
					questionKey:
						"templates.ai-background-remover.faq.items.processingTime.question",
					answerKey:
						"templates.ai-background-remover.faq.items.processingTime.answer",
				},
				{
					questionKey:
						"templates.ai-background-remover.faq.items.quality.question",
					answerKey:
						"templates.ai-background-remover.faq.items.quality.answer",
				},
				{
					questionKey:
						"templates.ai-background-remover.faq.items.privacy.question",
					answerKey:
						"templates.ai-background-remover.faq.items.privacy.answer",
				},
				{
					questionKey:
						"templates.ai-background-remover.faq.items.commercial.question",
					answerKey:
						"templates.ai-background-remover.faq.items.commercial.answer",
				},
				{
					questionKey:
						"templates.ai-background-remover.faq.items.deleteContent.question",
					answerKey:
						"templates.ai-background-remover.faq.items.deleteContent.answer",
				},
			],
			testimonials: [
				{
					nameKey:
						"templates.ai-background-remover.testimonials.user1.name",
					roleKey:
						"templates.ai-background-remover.testimonials.user1.role",
					contentKey:
						"templates.ai-background-remover.testimonials.user1.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.ai-background-remover.testimonials.user2.name",
					roleKey:
						"templates.ai-background-remover.testimonials.user2.role",
					contentKey:
						"templates.ai-background-remover.testimonials.user2.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.ai-background-remover.testimonials.user3.name",
					roleKey:
						"templates.ai-background-remover.testimonials.user3.role",
					contentKey:
						"templates.ai-background-remover.testimonials.user3.content",
					rating: 5,
				},
			],
			ctaTitleKey: "templates.ai-background-remover.cta.title",
			ctaDescriptionKey:
				"templates.ai-background-remover.cta.description",
		},
	},
	{
		id: "ai-smile-generator",
		publishedAt: "2026-01-26",
		updatedAt: "2026-10-26",
		name: "AI Smile Generator",
		description: "Add natural smiles to any photo with AI",
		prompt: "Add a natural, authentic smile to the person in the photo, maintaining facial features and natural expression, realistic teeth, subtle smile lines, warm and genuine expression",
		category: "personal",
		thumbnail: imageEffectSamples.smile[0].after,
		exampleImages: [
			buildR2AssetsImageUrl(
				"templates/ai-smile-generator/carousel/before-01.webp",
			),
		],
		behavior: {
			defaultAspectRatio: "3:4",
			disableOriginalAspectRatio: true,
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						buildR2AssetsImageUrl(
							"templates/ai-smile-generator/carousel/before-01.webp",
						),
					],
					result: imageEffectSamples.smile[0].after,
				},
				style: {
					theme: "professional",
				},
			},
			model: {
				default: "v3.0",
			},
			maxImages: 1,
		},
		marketingContent: {
			useCases: imageEffectSamples.smile.map((example) => ({
				titleKey: `templates.ai-smile-generator.useCases.${example.key}.title`,
				descriptionKey: `templates.ai-smile-generator.useCases.${example.key}.description`,
				example: { before: example.before, after: example.after },
			})),
			carouselSlides: imageEffectSamples.smile.slice(0, 2).map((example) => ({
				referenceImages: [example.before],
				resultImage: example.after,
				prompt: example.prompt,
			})),
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.ai-smile-generator.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.ai-smile-generator.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.ai-smile-generator.howItWorks.steps.process.title",
					descriptionKey:
						"templates.ai-smile-generator.howItWorks.steps.process.description",
				},
				{
					step: 3,
					titleKey:
						"templates.ai-smile-generator.howItWorks.steps.download.title",
					descriptionKey:
						"templates.ai-smile-generator.howItWorks.steps.download.description",
				},
			],
			faqItems: [
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.isFree.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.isFree.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.skills.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.skills.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.processingTime.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.processingTime.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.quality.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.quality.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.privacy.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.privacy.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.commercial.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.commercial.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.groupPhotos.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.groupPhotos.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.smileIntensity.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.smileIntensity.answer",
				},
				{
					questionKey:
						"templates.ai-smile-generator.faq.items.deleteContent.question",
					answerKey:
						"templates.ai-smile-generator.faq.items.deleteContent.answer",
				},
			],
			testimonials: [
				{
					nameKey:
						"templates.ai-smile-generator.testimonials.user1.name",
					roleKey:
						"templates.ai-smile-generator.testimonials.user1.role",
					contentKey:
						"templates.ai-smile-generator.testimonials.user1.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.ai-smile-generator.testimonials.user2.name",
					roleKey:
						"templates.ai-smile-generator.testimonials.user2.role",
					contentKey:
						"templates.ai-smile-generator.testimonials.user2.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.ai-smile-generator.testimonials.user3.name",
					roleKey:
						"templates.ai-smile-generator.testimonials.user3.role",
					contentKey:
						"templates.ai-smile-generator.testimonials.user3.content",
					rating: 5,
				},
			],
			ctaTitleKey: "templates.ai-smile-generator.cta.title",
			ctaDescriptionKey: "templates.ai-smile-generator.cta.description",
		},
	},
	{
		id: "virtual-staging-ai",
		publishedAt: "2026-01-31",
		updatedAt: "2026-10-25",
		name: "Virtual Staging AI",
		description: "Virtually stage empty rooms with AI furniture and decor",
		prompt: "Scandinavian interior style, add a few indoor plants and warm lighting to make the room feel cozier. Include a light oak sofa, wooden coffee table and linen armchair.",
		category: "personal",
		thumbnail: buildR2AssetsImageUrl(
			"templates/virtual-staging-ai/carousel/after-01.webp",
		),
		exampleImages: [
			buildR2AssetsImageUrl(
				"templates/virtual-staging-ai/carousel/before-01.webp",
			),
		],
		behavior: {
			defaultAspectRatio: "1:1",
			disableOriginalAspectRatio: true,
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/carousel/before-01.webp",
						),
					],
					result: buildR2AssetsImageUrl(
						"templates/virtual-staging-ai/carousel/after-01.webp",
					),
				},
				style: {
					theme: "professional",
				},
			},
			// ⚠️ 这里的模型/分辨率被 i18n 文案写死引用了：virtual-staging-ai 的
			// faq.items.isFree.answer 在 en/ja/zh/zh-TW 里写着「每个房间 4 积分、
			// 450 积分约 110 个房间」（es/pt-BR 没有这个模板的译文）。这里不设
			// resolution，所以落到模型自带的 1K（nano-banana-2-1 的 1K 是 4 分）。
			// **加 resolution.default 或换模型之前，先改那几条文案**，否则它们会
			// 静默变成假声明，没有测试会拦住。
			model: {
				default: "nano-banana-2-1",
			},
			maxImages: 1,
		},
		marketingContent: {
			useCases: [
				{
					titleKey:
						"templates.virtual-staging-ai.useCases.realEstate.title",
					descriptionKey:
						"templates.virtual-staging-ai.useCases.realEstate.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/realEstate-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/realEstate-after.webp",
						),
						prompt: "Scandinavian interior style, add a few indoor plants and warm lighting to make the room feel cozier. Include a light oak sofa, wooden coffee table and linen armchair.",
					},
					icon: "briefcase",
					iconColor: "text-blue-600 dark:text-blue-400",
					bgGradient:
						"from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20",
				},
				{
					titleKey:
						"templates.virtual-staging-ai.useCases.interiorDesign.title",
					descriptionKey:
						"templates.virtual-staging-ai.useCases.interiorDesign.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/interiorDesign-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/interiorDesign-after.webp",
						),
						prompt: "Japandi interior style, add paper lanterns, a single large plant and neutral textiles. Include a low dining table, floor cushions and wooden storage.",
					},
					icon: "palette",
					iconColor: "text-purple-600 dark:text-purple-400",
					bgGradient:
						"from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20",
				},
				{
					titleKey:
						"templates.virtual-staging-ai.useCases.homeRenovation.title",
					descriptionKey:
						"templates.virtual-staging-ai.useCases.homeRenovation.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/homeRenovation-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/homeRenovation-after.webp",
						),
						prompt: "Coastal interior style, add a cream sofa, pale blue cushions, a light wood coffee table and a woven rug. Keep the room structure, windows, doors, camera angle and existing fixtures unchanged.",
					},
					icon: "sparkles",
					iconColor: "text-amber-600 dark:text-amber-400",
					bgGradient:
						"from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20",
				},
				{
					titleKey:
						"templates.virtual-staging-ai.useCases.propertyMarketing.title",
					descriptionKey:
						"templates.virtual-staging-ai.useCases.propertyMarketing.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/propertyMarketing-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/propertyMarketing-after.webp",
						),
						prompt: "Modern interior style, add an inviting cream lounge chair, walnut side table, floor lamp and one large plant. Keep the room structure, windows, doors, camera angle and existing fixtures unchanged.",
					},
					icon: "trending-up",
					iconColor: "text-green-600 dark:text-green-400",
					bgGradient:
						"from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20",
				},
				{
					titleKey:
						"templates.virtual-staging-ai.useCases.photography.title",
					descriptionKey:
						"templates.virtual-staging-ai.useCases.photography.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/photography-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/photography-after.webp",
						),
						prompt: "Scandinavian interior style, add a linen sofa, oak coffee table, two matching armchairs and a neutral rug. Keep the room structure, windows, doors, camera angle and existing fixtures unchanged.",
					},
					icon: "camera",
					iconColor: "text-indigo-600 dark:text-indigo-400",
					bgGradient:
						"from-indigo-50 to-indigo-100 dark:from-indigo-950/20 dark:to-indigo-900/20",
				},
				{
					titleKey:
						"templates.virtual-staging-ai.useCases.architectureVisualization.title",
					descriptionKey:
						"templates.virtual-staging-ai.useCases.architectureVisualization.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/architectureVisualization-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/use-cases/architectureVisualization-after.webp",
						),
						prompt: "Minimalist interior style, add a simple oak dining table, four slim chairs, a paper pendant lamp and one plant. Keep the room structure, windows, doors, camera angle and existing fixtures unchanged.",
					},
					icon: "award",
					iconColor: "text-teal-600 dark:text-teal-400",
					bgGradient:
						"from-teal-50 to-teal-100 dark:from-teal-950/20 dark:to-teal-900/20",
				},
			],
			carouselSlides: [
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/carousel/before-01.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/virtual-staging-ai/carousel/after-01.webp",
					),
					prompt: "Scandinavian interior style, add a few indoor plants and warm lighting to make the room feel cozier. Include a light oak sofa, wooden coffee table and linen armchair.",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/carousel/before-02.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/virtual-staging-ai/carousel/slide-02-result-v2.webp",
					),
					prompt: "Japandi interior style, add paper lanterns, a single large plant and neutral textiles. Include a low platform bed, wooden nightstand and minimalist decor.",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/carousel/before-02.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/virtual-staging-ai/use-cases/interiorDesign-after.webp",
					),
					prompt: "Japandi interior style, add paper lanterns, a single large plant and neutral textiles. Include a low dining table, floor cushions and wooden storage.",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/virtual-staging-ai/carousel/before-01.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/virtual-staging-ai/carousel/slide-04-result-v2.webp",
					),
					prompt: "Scandinavian interior style home office, add warm desk lamp and indoor plants. Include a light wood desk, ergonomic chair, open shelving and minimalist workspace organization.",
				},
			],
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.virtual-staging-ai.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.virtual-staging-ai.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.virtual-staging-ai.howItWorks.steps.describe.title",
					descriptionKey:
						"templates.virtual-staging-ai.howItWorks.steps.describe.description",
				},
				{
					step: 3,
					titleKey:
						"templates.virtual-staging-ai.howItWorks.steps.generate.title",
					descriptionKey:
						"templates.virtual-staging-ai.howItWorks.steps.generate.description",
				},
			],
			faqItems: [
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.whatIsIt.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.whatIsIt.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.isFree.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.isFree.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.processingTime.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.processingTime.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.styles.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.styles.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.quality.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.quality.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.commercial.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.commercial.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.photoRequirements.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.photoRequirements.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.privacy.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.privacy.answer",
				},
				{
					questionKey:
						"templates.virtual-staging-ai.faq.items.deleteContent.question",
					answerKey:
						"templates.virtual-staging-ai.faq.items.deleteContent.answer",
				},
			],
			testimonials: [
				{
					nameKey:
						"templates.virtual-staging-ai.testimonials.user1.name",
					roleKey:
						"templates.virtual-staging-ai.testimonials.user1.role",
					contentKey:
						"templates.virtual-staging-ai.testimonials.user1.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.virtual-staging-ai.testimonials.user2.name",
					roleKey:
						"templates.virtual-staging-ai.testimonials.user2.role",
					contentKey:
						"templates.virtual-staging-ai.testimonials.user2.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.virtual-staging-ai.testimonials.user3.name",
					roleKey:
						"templates.virtual-staging-ai.testimonials.user3.role",
					contentKey:
						"templates.virtual-staging-ai.testimonials.user3.content",
					rating: 5,
				},
			],
			ctaTitleKey: "templates.virtual-staging-ai.cta.title",
			ctaDescriptionKey: "templates.virtual-staging-ai.cta.description",
		},
	},
	{
		id: "ai-pose-generator",
		publishedAt: "2026-03-20",
		updatedAt: "2026-10-26",
		name: "AI Pose Generator",
		description: "Change body poses in photos realistically with AI",
		prompt: "Change the person's pose to sitting on a tall stool, leaning forward with elbows on knees and hands clasped, looking at the camera. Preserve the original outfit, hairstyle, and facial features.",
		category: "personal",
		thumbnail: buildR2AssetsImageUrl(
			"templates/ai-pose-generator/use-cases/photography-after.webp",
		),
		exampleImages: [
			buildR2AssetsImageUrl(
				"templates/ai-pose-generator/use-cases/photography-before.webp",
			),
		],
		behavior: {
			defaultAspectRatio: "1:1",
			disableOriginalAspectRatio: false,
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/photography-before.webp",
						),
					],
					result: buildR2AssetsImageUrl(
						"templates/ai-pose-generator/use-cases/photography-after.webp",
					),
				},
				style: {
					theme: "professional",
				},
			},
			model: {
				default: "v3.0",
				allowed: ["v3.0"],
			},
			resolution: {
				default: "2K",
				allowed: ["1K", "2K", "4K"],
			},
			maxImages: 2,
		},
		marketingContent: {
			useCases: [
				{
					titleKey:
						"templates.ai-pose-generator.useCases.photographyReference.title",
					descriptionKey:
						"templates.ai-pose-generator.useCases.photographyReference.description",
					icon: "camera",
					iconColor: "text-blue-600 dark:text-blue-400",
					bgGradient:
						"from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/photography-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/photography-after.webp",
						),
						prompt: "sitting on a tall stool, leaning forward with elbows on knees, hands clasped, looking at the camera",
					},
				},
				{
					titleKey:
						"templates.ai-pose-generator.useCases.fashionEcommerce.title",
					descriptionKey:
						"templates.ai-pose-generator.useCases.fashionEcommerce.description",
					icon: "briefcase",
					iconColor: "text-purple-600 dark:text-purple-400",
					bgGradient:
						"from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/fashion-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/fashion-after.webp",
						),
						prompt: "three-quarter turn, one hand in the jacket pocket, the other touching the collar, weight on the back leg",
					},
				},
				{
					titleKey:
						"templates.ai-pose-generator.useCases.illustrationArt.title",
					descriptionKey:
						"templates.ai-pose-generator.useCases.illustrationArt.description",
					icon: "palette",
					iconColor: "text-amber-600 dark:text-amber-400",
					bgGradient:
						"from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/illustration-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/illustration-after.webp",
						),
						prompt: "full-body dynamic pose, leaping forward with one arm reaching out, hair flowing",
					},
				},
				{
					titleKey:
						"templates.ai-pose-generator.useCases.contentCreation.title",
					descriptionKey:
						"templates.ai-pose-generator.useCases.contentCreation.description",
					icon: "users",
					iconColor: "text-pink-600 dark:text-pink-400",
					bgGradient:
						"from-pink-50 to-pink-100 dark:from-pink-950/20 dark:to-pink-900/20",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/social-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/social-after.webp",
						),
						prompt: "making a peace sign near her face, head tilted, big smile",
					},
				},
				{
					titleKey:
						"templates.ai-pose-generator.useCases.fitnessCoaching.title",
					descriptionKey:
						"templates.ai-pose-generator.useCases.fitnessCoaching.description",
					icon: "zap",
					iconColor: "text-green-600 dark:text-green-400",
					bgGradient:
						"from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/fitness-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/fitness-after.webp",
						),
						prompt: "deep bodyweight squat, arms straight out in front, back straight, heels on the floor",
					},
				},
				{
					titleKey:
						"templates.ai-pose-generator.useCases.animationDesign.title",
					descriptionKey:
						"templates.ai-pose-generator.useCases.animationDesign.description",
					icon: "sparkles",
					iconColor: "text-indigo-600 dark:text-indigo-400",
					bgGradient:
						"from-indigo-50 to-indigo-100 dark:from-indigo-950/20 dark:to-indigo-900/20",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/animation-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/ai-pose-generator/use-cases/animation-after.webp",
						),
						prompt: "standing and waving with one hand, the other hand on his hip, cheerful",
					},
				},
			],
			carouselSlides: [
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-pose-generator/carousel/slide-01-reference-01.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-pose-generator/carousel/slide-01-result-v2.webp",
					),
					prompt: "natural seated pose on a chair, relaxed shoulders, feet flat on the floor, hands resting naturally, calm expression",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-pose-generator/carousel/slide-02-reference-01.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-pose-generator/carousel/slide-02-result.webp",
					),
					prompt: "walking pose, one leg stepping forward naturally, arms moving softly with the body, relaxed confident expression, full body",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/ai-pose-generator/carousel/slide-03-reference-01.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/ai-pose-generator/carousel/slide-03-result.webp",
					),
					prompt: "slight contrapposto standing pose, one hip shifted naturally, one arm relaxed and the other lightly bent, elegant posture, soft expression",
				},
			],
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.ai-pose-generator.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.ai-pose-generator.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.ai-pose-generator.howItWorks.steps.describe.title",
					descriptionKey:
						"templates.ai-pose-generator.howItWorks.steps.describe.description",
				},
				{
					step: 3,
					titleKey:
						"templates.ai-pose-generator.howItWorks.steps.generate.title",
					descriptionKey:
						"templates.ai-pose-generator.howItWorks.steps.generate.description",
				},
			],
			faqItems: [
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.whatIsIt.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.whatIsIt.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.isFree.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.isFree.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.quality.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.quality.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.inputTypes.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.inputTypes.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.multiPerson.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.multiPerson.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.processingTime.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.processingTime.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.identity.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.identity.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.commercial.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.commercial.answer",
				},
				{
					questionKey:
						"templates.ai-pose-generator.faq.items.deleteContent.question",
					answerKey:
						"templates.ai-pose-generator.faq.items.deleteContent.answer",
				},
			],
			testimonials: [
				{
					nameKey:
						"templates.ai-pose-generator.testimonials.user1.name",
					roleKey:
						"templates.ai-pose-generator.testimonials.user1.role",
					contentKey:
						"templates.ai-pose-generator.testimonials.user1.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.ai-pose-generator.testimonials.user2.name",
					roleKey:
						"templates.ai-pose-generator.testimonials.user2.role",
					contentKey:
						"templates.ai-pose-generator.testimonials.user2.content",
					rating: 5,
				},
				{
					nameKey:
						"templates.ai-pose-generator.testimonials.user3.name",
					roleKey:
						"templates.ai-pose-generator.testimonials.user3.role",
					contentKey:
						"templates.ai-pose-generator.testimonials.user3.content",
					rating: 5,
				},
			],
			ctaTitleKey: "templates.ai-pose-generator.cta.title",
			ctaDescriptionKey: "templates.ai-pose-generator.cta.description",
		},
	},
	{
		id: "image-to-emoji-converter",
		publishedAt: "2026-09-22",
		updatedAt: "2026-09-22",
		name: "Image to Emoji Converter",
		description: "Turn a photo into a custom emoji-style image with AI",
		prompt: "Turn the main subject in the uploaded image into one charming custom 3D emoji-style sticker illustration. Keep the person, pet, or object recognizable through its defining face, hair, colors, clothing, or shape. Simplify fine details into smooth rounded shapes with expressive features and soft dimensional shading. Center one subject in a square frame, filling most of the image while keeping the full head or object visible. Add a crisp thick white die-cut sticker outline around the subject's silhouette. Behind the outlined subject, use a softly blurred pastel sky-blue to pale mint-green gradient with no photographic scenery. Make the result clear at small chat size. No text, frame, extra subjects, collage, checkerboard, or watermark.",
		category: "personal",
		thumbnail: "/images/image-to-emoji/nano-banana-2.jpg",
		behavior: {
			defaultAspectRatio: "1:1",
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						"/images/photo-to-cartoon/woman-black-portrait.webp",
					],
					result: "/images/image-to-emoji/nano-banana-2.jpg",
				},
				style: { theme: "creative" },
			},
			model: {
				default: "nano-banana-2",
				allowed: ["nano-banana-2"],
			},
			resolution: {
				default: "1K",
				allowed: ["1K", "2K", "4K"],
			},
			maxImages: 1,
			hidePrompt: true,
		},
		marketingContent: {
			hideTestimonials: true,
			useCases: [
				{
					titleKey:
						"templates.image-to-emoji-converter.useCases.profile.title",
					descriptionKey:
						"templates.image-to-emoji-converter.useCases.profile.description",
					example: { before: "/images/photo-to-cartoon/woman-black-portrait.webp", after: "/images/image-to-emoji/nano-banana-2.jpg" },
					icon: "users",
				},
				{
					titleKey:
						"templates.image-to-emoji-converter.useCases.chat.title",
					descriptionKey:
						"templates.image-to-emoji-converter.useCases.chat.description",
					example: { before: "/images/image-to-emoji/man-headshot.jpg", after: "/images/image-to-emoji/man-emoji.jpg" },
					icon: "heart",
				},
				{
					titleKey:
						"templates.image-to-emoji-converter.useCases.pet.title",
					descriptionKey:
						"templates.image-to-emoji-converter.useCases.pet.description",
					example: { before: imageEffectSamples.emoji[0].before, after: imageEffectSamples.emoji[0].after },
					icon: "image",
				},
			],
			carouselSlides: [
				{
					referenceImages: [
						"/images/photo-to-cartoon/woman-black-portrait.webp",
					],
					resultImage: "/images/image-to-emoji/nano-banana-2.jpg",
					prompt: "Convert the person in the uploaded reference photo into one charming 3D emoji sticker illustration. Keep her recognizable brown wavy hair, oval face, calm expression, black top, and hand near her hair. Use rounded simplified facial features, warm skin tones, and soft dimensional shading. Center her head and upper torso in a square frame, with the full head visible and the character filling most of the image. Add a crisp, thick white die-cut sticker outline that follows the hair, shoulders, and body silhouette. Behind the outlined character, use a softly blurred pastel sky-blue to pale mint-green gradient with no photographic scenery. The result should read clearly as one custom emoji at chat size. No text, frame, extra people, collage, checkerboard, or watermark.",
				},
				{
					referenceImages: [
						"/images/image-to-emoji/man-headshot.jpg",
					],
					resultImage: "/images/image-to-emoji/man-emoji.jpg",
					prompt: "Convert the man in the uploaded headshot into ONE charming custom 3D emoji sticker portrait. Preserve his recognizable short dark brown hair, light stubble, friendly toothy smile, dark eyes, and black jacket. Show his face and shoulders straight on, with the head large and centered: the face should occupy about half the square image while the full hair shape remains visible. Use rounded simplified facial features, warm natural skin tones, expressive eyes, and soft dimensional shading. Put a crisp thick white die-cut sticker outline around the hair and shoulders. Behind the outline, use a softly blurred pastel sky-blue to pale mint-green gradient. Make it read clearly as a single emoji at chat size. The frame is clean and text-free, with no extra people, photographic room, or watermark.",
				},
				{
					referenceImages: [
						"/images/image-to-emoji/smiling-woman-headshot.jpg",
					],
					resultImage:
						"/images/image-to-emoji/smiling-woman-emoji.jpg",
					prompt: "Convert the woman in the uploaded close-up headshot into ONE charming custom 3D emoji sticker portrait. Preserve her recognizable curly auburn hair, side glance, wide natural smile, fair skin, and muted green top. Show her head and shoulders in a gentle three-quarter pose, with the head large and centered: the face should occupy about half the square image while the full hair shape remains visible. Use rounded simplified facial features, expressive eyes, and soft dimensional shading. Put a crisp thick white die-cut sticker outline around the curls and shoulders. Behind the outline, use a softly blurred pastel peach-to-lavender gradient. Make it read clearly as a single emoji at chat size. The frame is clean and text-free, with no extra people, photographic room, or watermark.",
				},
				{
					referenceImages: [imageEffectSamples.emoji[0].before],
					resultImage: imageEffectSamples.emoji[0].after,
					prompt: imageEffectSamples.emoji[0].prompt,
				},
			],
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.image-to-emoji-converter.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.image-to-emoji-converter.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.image-to-emoji-converter.howItWorks.steps.generate.title",
					descriptionKey:
						"templates.image-to-emoji-converter.howItWorks.steps.generate.description",
				},
				{
					step: 3,
					titleKey:
						"templates.image-to-emoji-converter.howItWorks.steps.download.title",
					descriptionKey:
						"templates.image-to-emoji-converter.howItWorks.steps.download.description",
				},
			],
			faqItems: [
				"howTo",
				"photo",
				"resolution",
				"transparent",
				"batch",
				"free",
				"deleteContent",
			].map((key) => ({
				questionKey: `templates.image-to-emoji-converter.faq.items.${key}.question`,
				answerKey: `templates.image-to-emoji-converter.faq.items.${key}.answer`,
			})),
			ctaTitleKey: "templates.image-to-emoji-converter.cta.title",
			ctaDescriptionKey:
				"templates.image-to-emoji-converter.cta.description",
		},
	},
	{
		id: "getty-images-watermark-remover",
		publishedAt: "2026-09-08",
		updatedAt: "2026-10-25",
		name: "Getty Images Watermark Remover",
		description:
			"Remove the diagonal Getty Images watermark from images you're licensed to use",
		prompt: "Remove the diagonal 'gettyimages' wordmark and the small ID number near it. Rebuild the background naturally where the watermark was. Keep everything else in the image — composition, subject, colors, lighting — exactly unchanged.",
		category: "personal",
		thumbnail: buildR2AssetsImageUrl(
			"templates/getty-images-watermark-remover/carousel/before-landscape.webp",
		),
		exampleImages: [
			buildR2AssetsImageUrl(
				"templates/getty-images-watermark-remover/carousel/before-landscape.webp",
			),
		],
		behavior: {
			// 去水印必须原样返回构图，不能像其它模板那样钉死 1:1——实测钉 1:1 时
			// 1200x800 的输入会被重绘成 1024x1024，构图整个变掉。"auto" 是上游的
			// 「跟随输入图」，gpt-image-2 与 nano 系列都支持（supportsAutoAspectRatio）。
			defaultAspectRatio: "auto",
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [
						buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/carousel/before-landscape.webp",
						),
					],
					result: buildR2AssetsImageUrl(
						"templates/getty-images-watermark-remover/carousel/after-landscape.webp",
					),
				},
				style: {
					theme: "professional",
				},
			},
			model: {
				default: "gpt-image-2",
			},
			maxImages: 1,
			hidePrompt: true,
			// 比例 / 清晰度 / 张数三个选择器都保留给用户自己调，默认走 Auto。
		},
		marketingContent: {
			useCases: [
				{
					titleKey:
						"templates.getty-images-watermark-remover.useCases.previewCleanup.title",
					descriptionKey:
						"templates.getty-images-watermark-remover.useCases.previewCleanup.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/use-cases/previewCleanup-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/use-cases/previewCleanup-after.webp",
						),
					},
					icon: "image",
					iconColor: "text-blue-600 dark:text-blue-400",
					bgGradient:
						"from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20",
				},
				{
					titleKey:
						"templates.getty-images-watermark-remover.useCases.presentationComps.title",
					descriptionKey:
						"templates.getty-images-watermark-remover.useCases.presentationComps.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/use-cases/presentationComps-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/use-cases/presentationComps-after.webp",
						),
					},
					icon: "briefcase",
					iconColor: "text-purple-600 dark:text-purple-400",
					bgGradient:
						"from-purple-50 to-purple-100 dark:from-purple-950/20 dark:to-purple-900/20",
				},
				{
					titleKey:
						"templates.getty-images-watermark-remover.useCases.licensedArchive.title",
					descriptionKey:
						"templates.getty-images-watermark-remover.useCases.licensedArchive.description",
					example: {
						before: buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/use-cases/licensedArchive-before.webp",
						),
						after: buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/use-cases/licensedArchive-after.webp",
						),
					},
					icon: "calendar",
					iconColor: "text-green-600 dark:text-green-400",
					bgGradient:
						"from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20",
				},
			],
			carouselSlides: [
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/carousel/before-04.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/getty-images-watermark-remover/carousel/after-04.webp",
					),
					prompt: "Strip the bracketed copyright stamps, keep architecture and water detail intact",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/carousel/before-sports.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/getty-images-watermark-remover/carousel/after-sports.webp",
					),
					prompt: "Remove the single diagonal wordmark and its ID number, rebuild the track and stadium behind it",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/carousel/before-beauty.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/getty-images-watermark-remover/carousel/after-beauty.webp",
					),
					prompt: "Clear the repeating tiled watermark across the whole frame, keep skin and hair detail sharp",
				},
				{
					referenceImages: [
						buildR2AssetsImageUrl(
							"templates/getty-images-watermark-remover/carousel/before-landscape.webp",
						),
					],
					resultImage: buildR2AssetsImageUrl(
						"templates/getty-images-watermark-remover/carousel/after-landscape.webp",
					),
					prompt: "Remove the dashed grid overlay and its labels, restore the sky and water underneath",
				},
			],
			howItWorks: [
				{
					step: 1,
					titleKey:
						"templates.getty-images-watermark-remover.howItWorks.steps.upload.title",
					descriptionKey:
						"templates.getty-images-watermark-remover.howItWorks.steps.upload.description",
				},
				{
					step: 2,
					titleKey:
						"templates.getty-images-watermark-remover.howItWorks.steps.remove.title",
					descriptionKey:
						"templates.getty-images-watermark-remover.howItWorks.steps.remove.description",
				},
				{
					step: 3,
					titleKey:
						"templates.getty-images-watermark-remover.howItWorks.steps.download.title",
					descriptionKey:
						"templates.getty-images-watermark-remover.howItWorks.steps.download.description",
				},
			],
			faqItems: [
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.howTo.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.howTo.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.isFree.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.isFree.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.batch.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.batch.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.oneWord.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.oneWord.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.withoutBlur.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.withoutBlur.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.legal.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.legal.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.embed.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.embed.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.licenseTypes.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.licenseTypes.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.quality.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.quality.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.seam.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.seam.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.otherStockSites.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.otherStockSites.answer",
				},
				{
					questionKey:
						"templates.getty-images-watermark-remover.faq.items.deleteContent.question",
					answerKey:
						"templates.getty-images-watermark-remover.faq.items.deleteContent.answer",
				},
			],
			ctaTitleKey: "templates.getty-images-watermark-remover.cta.title",
			ctaDescriptionKey:
				"templates.getty-images-watermark-remover.cta.description",
		},
	},
	{
		id: "ai-muscle-generator",
		publishedAt: "2026-10-29",
		updatedAt: "2026-10-29",
		name: "AI Muscle Generator",
		description: "Add realistic muscles to any photo with AI",
		prompt: MUSCLE_PROMPTS.athletic,
		category: "personal",
		thumbnail: muscleExamples[0].after,
		exampleImages: [muscleRef("man-tee")],
		behavior: {
			defaultAspectRatio: "auto",
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [muscleExamples[0].before],
					result: muscleExamples[0].after,
				},
				style: { theme: "creative" },
			},
			model: {
				default: "nano-banana-2-1",
				allowed: ["nano-banana-2-1"],
			},
			resolution: {
				default: "1K",
				allowed: ["1K", "2K", "4K"],
			},
			maxImages: 1,
			hideOutputCount: true,
			promptPresets: [
				{ label: "Toned", prompt: MUSCLE_PROMPTS.toned },
				{ label: "Athletic", prompt: MUSCLE_PROMPTS.athletic },
				{ label: "Bodybuilder", prompt: MUSCLE_PROMPTS.bodybuilder },
				{ label: "Superhero", prompt: MUSCLE_PROMPTS.superhero },
			],
		},
		marketingContent: {
			hideTestimonials: true,
			useCases: muscleUseCases.map(({ key, ref, preset }) => ({
				titleKey: `templates.ai-muscle-generator.useCases.${key}.title`,
				descriptionKey: `templates.ai-muscle-generator.useCases.${key}.description`,
				example: {
					before: muscleRef(ref),
					after: muscleResult(`${ref}-${preset}`),
					prompt: MUSCLE_PROMPTS[preset],
				},
			})),
			carouselSlides: muscleExamples.map((example) => ({
				referenceImages: [example.before],
				resultImage: example.after,
				prompt: example.prompt,
			})),
			howItWorks: (["upload", "style", "download"] as const).map(
				(key, index) => ({
					step: index + 1,
					titleKey: `templates.ai-muscle-generator.howItWorks.steps.${key}.title`,
					descriptionKey: `templates.ai-muscle-generator.howItWorks.steps.${key}.description`,
				}),
			),
			ctaTitleKey: "templates.ai-muscle-generator.cta.title",
			ctaDescriptionKey: "templates.ai-muscle-generator.cta.description",
		},
	},
	{
		id: "ai-big-butt",
		publishedAt: "2026-10-29",
		updatedAt: "2026-10-31",
		name: "AI Big Butt Filter",
		description: "Make your butt bigger in any photo with AI",
		prompt: BUTT_PROMPTS.curvy,
		category: "personal",
		thumbnail: buttExamples[0].after,
		exampleImages: [buttRef("woman-leggings-v2")],
		behavior: {
			// Flash 上游没有 auto：先按样图的 2:3，上传后按图就近吸附
			defaultAspectRatio: "2:3",
			aspectRatioFollowsInput: true,
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [buttExamples[0].before],
					result: buttExamples[0].after,
				},
				style: { theme: "creative" },
			},
			model: {
				default: "seedream-5-flash",
				allowed: ["seedream-5-flash"],
			},
			// Flash 1K / 2K 同价 4 积分，默认 2K
			resolution: {
				default: "2K",
				allowed: ["1K", "2K"],
			},
			maxImages: 1,
			hideOutputCount: true,
			promptPresets: [
				{ label: "Subtle", prompt: BUTT_PROMPTS.subtle },
				{ label: "Curvy", prompt: BUTT_PROMPTS.curvy },
				{ label: "Extra", prompt: BUTT_PROMPTS.extra },
			],
		},
		marketingContent: {
			hideTestimonials: true,
			useCases: buttUseCases.map(({ key, ref, preset }) => ({
				titleKey: `templates.ai-big-butt.useCases.${key}.title`,
				descriptionKey: `templates.ai-big-butt.useCases.${key}.description`,
				example: {
					before: buttRef(ref),
					after: buttResult(`${ref}-${preset}`),
					prompt: BUTT_PROMPTS[preset],
				},
			})),
			carouselSlides: buttExamples.map((example) => ({
				referenceImages: [example.before],
				resultImage: example.after,
				prompt: example.prompt,
			})),
			howItWorks: (["upload", "style", "download"] as const).map(
				(key, index) => ({
					step: index + 1,
					titleKey: `templates.ai-big-butt.howItWorks.steps.${key}.title`,
					descriptionKey: `templates.ai-big-butt.howItWorks.steps.${key}.description`,
				}),
			),
			ctaTitleKey: "templates.ai-big-butt.cta.title",
			ctaDescriptionKey: "templates.ai-big-butt.cta.description",
		},
	},
	{
		id: "ai-feet-generator",
		publishedAt: "2026-10-29",
		updatedAt: "2026-11-01",
		name: "AI Feet Generator",
		description: "Create realistic or anime feet images from text or a photo",
		prompt: FEET_PROMPTS.soles,
		category: "personal",
		thumbnail: feetExamples[0].after,
		behavior: {
			defaultAspectRatio: "2:3",
			showCustomPreview: true,
			customPreview: {
				images: {
					reference: [feetExamples[0].before],
					result: feetExamples[0].after,
				},
				style: { theme: "creative" },
			},
			model: {
				default: "nano-banana-2-1",
				allowed: ["nano-banana-2-1"],
			},
			resolution: {
				default: "1K",
				allowed: ["1K", "2K", "4K"],
			},
			maxImages: 1,
			// 老公 10/29 选 B：不传图就按 prompt 文生图，传了照片就按照片里的人出
			optionalImage: true,
			hideOutputCount: true,
			promptPresets: [
				{ label: "Soles POV", prompt: FEET_PROMPTS.soles },
				{ label: "Beach POV", prompt: FEET_PROMPTS.beach },
				{ label: "Car Seat", prompt: FEET_PROMPTS.car },
				{ label: "Grass", prompt: FEET_PROMPTS.grass },
				{ label: "Pedicure", prompt: FEET_PROMPTS.pedicure },
				{ label: "Anime", prompt: FEET_PROMPTS.anime },
			],
		},
		marketingContent: {
			hideTestimonials: true,
			// 场景卡纯文字：Before/After 卡要原图，纯文字的例子没有；图都放在 Examples 区
			useCases: (
				["footwear", "pedicure", "reference", "anime"] as const
			).map((key) => ({
				titleKey: `templates.ai-feet-generator.useCases.${key}.title`,
				descriptionKey: `templates.ai-feet-generator.useCases.${key}.description`,
			})),
			carouselSlides: feetExamples.map((example) => ({
				referenceImages: [example.before],
				resultImage: example.after,
				prompt: example.prompt,
			})),
			// 和左栏、三步插图同序：先照片（可选）再 prompt（10/29 审查）
			howItWorks: (["photo", "describe", "download"] as const).map(
				(key, index) => ({
					step: index + 1,
					titleKey: `templates.ai-feet-generator.howItWorks.steps.${key}.title`,
					descriptionKey: `templates.ai-feet-generator.howItWorks.steps.${key}.description`,
				}),
			),
			ctaTitleKey: "templates.ai-feet-generator.cta.title",
			ctaDescriptionKey: "templates.ai-feet-generator.cta.description",
		},
	},
];

export function getTemplateById(id: string): Template | undefined {
	return templates.find((template) => template.id === id);
}
